import { PUBLIC_BETWATCH_API_ENDPOINT } from '$env/static/public';
import { type VariablesOf, graphql, type ResultOf } from 'gql.tada';
import { print } from 'graphql';

const SearchQuery = graphql(`
  query Search($input: AdvancedSearchInput!) {
    search(input: $input) {
      hasMore
      races {
        betfairMarkets {
          selectionId
          sp
          marketName
          totalMatched
        }
        matchedMarkets {
          id
          selectionId
          bookmaker
          fixedWin {
            price
            lastUpdated
            flucs {
              price
              lastUpdated
            }
          }
        }
        race {
          id
          number
          name
          status
          distance
          startTime
          results
          prizeMoney
          meeting {
            type
            location
            date
            type
            track
          }
          runners {
            id
            barrier
            scratchedTime
            riderName
            trainerName
            number
            name
          }
        }
      }
    }
  }
`);

export type SearchQueryVariables = VariablesOf<typeof SearchQuery>;
export type SearchQueryResult = ResultOf<typeof SearchQuery>;
export type ArrayElementType<T> = T extends (infer U)[] ? U : never;
export type SearchInputType = SearchQueryVariables['input'];
export type SearchInputItemType = ArrayElementType<SearchInputType['items']>;
export type SearchInputItemPriceOperatorType = ArrayElementType<
  SearchInputItemType['prices']
>['operator'];
export const SearchInputItemPriceOperatorTypeOptions = [
  {
    label: '=',
    value: 'EQUALS'
  },
  {
    label: '>',
    value: 'GREATER_THAN'
  },
  {
    label: '≥',
    value: 'GREATER_THAN_OR_EQUAL'
  },
  {
    label: '<',
    value: 'LESS_THAN'
  },
  {
    label: '≤',
    value: 'LESS_THAN_OR_EQUAL'
  }
] as const;

export const SearchInputItemOperatorTypeOptions = ['AND', 'OR'] as const;
export type SearchInputCodesTypeOption =
  | 'Thoroughbred'
  | 'Harness'
  | 'Greyhound';

export async function executeSearch(options: {
  fetch: typeof fetch;
  abortController: AbortController | null;
  auth: { accessToken: string };
  variables: SearchQueryVariables;
}) {
  const { fetch, variables, auth } = options;

  const response = await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    signal: options.abortController?.signal,
    method: 'POST',
    headers: {
      Authorization: `Bearer ${auth.accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      query: print(SearchQuery),
      variables: variables
    })
  });
  const json = (await response.json()) as {
    data: SearchQueryResult;
    errors?: { message: string }[];
  };
  return json;
}
